'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { cva } from 'class-variance-authority'
import { type PropsWithChildren, type ReactElement } from 'react'
import { Tag as AriaTag, Button, type TagProps as AriaTagProps } from 'react-aria-components'
import { Icon } from '../icons/icon/Icon'
import './Tag.css'

export type TagProps = UiComponentProps &
  StrictPick<AriaTagProps, 'isDisabled'> &
  PropsWithChildren & {
    iconLeft?: ReactElement
  }

const tagCva = cva(
  twMerge(
    'cursor-default',
    'inline-flex min-w-0 max-w-full items-center gap-1 overflow-hidden',
    'px-1 py-0.5 font-bold',
    'rounded-xs border-px border-solid border-clear hd:border-1.5',
    'data-[focus-visible]:outline-none',
    'group-data-[size=md]:text-md group-data-[size=md]:leading-light',
    'group-data-[size=sm]:text-sm group-data-[size=sm]:leading-lighter',
    'transition-[transform,background-color,border-color,color] duration-150 ease-out',
  ),
  {
    variants: {
      variant: {
        deletable: twMerge(
          'bg-brand-1-50 text-brand-2-900',
          'data-[focus-visible]:border-brand-1-800',
        ),
        selectable: twMerge(
          'cursor-pointer',
          'border-brand-2-200 text-brand-2-900',
          'data-[pressed]:scale-[0.98]',
          'data-[selected]:bg-brand-1-800 data-[selected]:data-[hovered]:bg-brand-1-50 data-[selected]:data-[hovered]:text-brand-1-800 data-[selected]:text-white',
          'data-[hovered]:border-brand-1-800',
          'data-[disabled]:pointer-events-none data-[disabled]:bg-brand-2-50 data-[disabled]:text-brand-2-400',
          'data-[focus-visible]:border-brand-1-800',
        ),
      },
    },
    defaultVariants: {
      variant: 'deletable',
    },
  },
)

const tagIconCva = twMerge(
  'shrink-0 [&_svg]:block',
  'group-data-[size=md]:[&_svg]:size-2',
  'group-data-[size=sm]:[&_svg]:size-[0.875rem]',
)

export function Tag({
  iconLeft,
  children,
  className,
  'data-testid': testId = 'tag',
  'aria-label': ariaLabel,
  ...props
}: TagProps) {
  const { t } = useTranslation(['tag'])
  const textValue =
    typeof children === 'string' || typeof children === 'number' ? String(children) : ariaLabel

  return (
    <AriaTag
      textValue={textValue}
      data-testid={testId}
      className={({ allowsRemoving, selectionMode }) =>
        twMerge(
          tagCva({
            variant: allowsRemoving
              ? 'deletable'
              : selectionMode !== 'none'
                ? 'selectable'
                : undefined,
          }),
          className,
        )
      }
      {...props}
    >
      {({ allowsRemoving }) => (
        <>
          {iconLeft && (
            <span data-testid={`${testId}/icon`} className={tagIconCva}>
              {iconLeft}
            </span>
          )}
          {children && (
            <span data-testid={`${testId}/text`} className="min-w-0 flex-1 select-none truncate">
              {children}
            </span>
          )}
          {allowsRemoving && (
            <Button
              slot="remove"
              data-testid={`${testId}/dismiss`}
              aria-label={t('tag:delete')}
              className={twMerge(
                tagIconCva,
                'bg-transparent pointer-events-auto shrink-0 cursor-pointer rounded-full border-0 p-0 text-brand-1-800 outline-none',
                'data-[hovered]:bg-brand-1-800 data-[hovered]:text-white',
              )}
            >
              <Icon.X size="sm" aria-hidden />
            </Button>
          )}
        </>
      )}
    </AriaTag>
  )
}
